import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { EQUIPMENT_TYPE_PRESETS } from "@/lib/equipmentTypes";
import {
  ClipboardCheck, Plus, Pencil, Trash2, X, GripVertical, CheckCircle2, EyeOff,
} from "lucide-react";

// Super-admin managed global cleaning protocols (like the Daily Tracking List).
// Each protocol = a checklist of steps, optionally scoped to equipment-type
// names. Empty scope = the general/default protocol.
const BLANK = { title: "", description: "", applies_to_type_names: [], steps: [""], is_active: true };

export default function CleaningProtocols() {
  const { confirm, notify } = useDialog();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/cleaning-protocols");
      setProtocols(data.protocols || []);
    } catch (e) { console.debug("protocols load failed", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...BLANK, steps: [""] }); setEditingId(null); setErr(""); };
  const openEdit = (p) => {
    setForm({
      title: p.title, description: p.description || "",
      applies_to_type_names: [...(p.applies_to_type_names || [])],
      steps: (p.steps || []).map((s) => s.text),
      is_active: p.is_active !== false,
    });
    setEditingId(p.id); setErr("");
  };

  const setStep = (i, v) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => (idx === i ? v : s)) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, ""] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const toggleType = (name) => setForm((f) => ({
    ...f,
    applies_to_type_names: f.applies_to_type_names.includes(name)
      ? f.applies_to_type_names.filter((n) => n !== name)
      : [...f.applies_to_type_names, name],
  }));

  const save = async (e) => {
    e.preventDefault(); setErr("");
    const steps = form.steps.map((s) => s.trim()).filter(Boolean);
    if (!form.title.trim()) return setErr("Title is required.");
    if (steps.length === 0) return setErr("Add at least one cleaning step.");
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(), description: form.description.trim(),
        applies_to_type_names: form.applies_to_type_names, steps, is_active: form.is_active,
      };
      if (editingId) await api.put(`/admin/cleaning-protocols/${editingId}`, payload);
      else await api.post("/admin/cleaning-protocols", payload);
      setForm(null); setEditingId(null);
      await load();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!await confirm({
      title: "Delete this protocol?",
      message: `"${p.title}" will be removed. Historical cleaning logs keep their snapshot and are unaffected.`,
      danger: true, confirmLabel: "Delete",
    })) return;
    try { await api.delete(`/admin/cleaning-protocols/${p.id}`); await load(); }
    catch (ex) { await notify({ title: "Delete failed", message: formatErr(ex.response?.data?.detail), variant: "err" }); }
  };

  return (
    <div className="max-w-5xl" data-testid="cleaning-protocols-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={22} style={{ color: "var(--vb-text-2)" }} />
          <div>
            <h1 className="vb-heading text-3xl m-0" style={{ color: "var(--vb-text-2)" }}>Cleaning Protocols</h1>
            <p className="text-sm opacity-70 m-0">Global cleaning checklists operators review when logging equipment cleaning. Scope a protocol to equipment types, or leave it unscoped as the general default.</p>
          </div>
        </div>
        <button className="vb-btn" onClick={openAdd} data-testid="protocol-add"><Plus size={15} /> New protocol</button>
      </div>

      {loading ? (
        <div className="opacity-60 text-sm py-8 text-center">Loading protocols…</div>
      ) : protocols.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="protocols-empty">No protocols yet. Create the first one.</div>
      ) : (
        <div className="grid gap-4" data-testid="protocols-list">
          {protocols.map((p) => (
            <div key={p.id} className="vb-card p-5" data-testid={`protocol-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-lg" style={{ color: "var(--vb-text-2)" }}>{p.title}</strong>
                    {p.is_active === false && <span className="vb-pill text-[10px] inline-flex items-center gap-1"><EyeOff size={10} /> Inactive</span>}
                  </div>
                  {p.description && <p className="text-sm opacity-70 mt-1 mb-0">{p.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(p.applies_to_type_names || []).length === 0
                      ? <span className="vb-pill text-[11px]" style={{ background: "var(--vb-bg-2)" }}>General default (all equipment)</span>
                      : p.applies_to_type_names.map((n) => <span key={n} className="vb-pill text-[11px]" style={{ background: "var(--vb-bg-2)" }}>{n}</span>)}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button className="vb-btn ghost sm" onClick={() => openEdit(p)} data-testid={`protocol-edit-${p.id}`}><Pencil size={13} /></button>
                  <button className="vb-btn ghost sm" onClick={() => del(p)} style={{ color: "var(--vb-error)" }} data-testid={`protocol-delete-${p.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              <ol className="mt-3 text-sm space-y-1 pl-5" style={{ listStyle: "decimal" }}>
                {(p.steps || []).map((s) => <li key={s.key}>{s.text}</li>)}
              </ol>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="vb-modal-bg" onClick={() => setForm(null)} data-testid="protocol-modal-bg">
          <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="protocol-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="vb-heading text-xl m-0">{editingId ? "Edit protocol" : "New protocol"}</h2>
              <button className="vb-btn ghost" onClick={() => setForm(null)} data-testid="protocol-close"><X size={16} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              {err && <div className="vb-pill err">{err}</div>}
              <div>
                <label className="text-xs uppercase opacity-70">Title *</label>
                <input className="vb-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Fryer Deep Clean" data-testid="protocol-title" />
              </div>
              <div>
                <label className="text-xs uppercase opacity-70">Description</label>
                <input className="vb-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Short summary" data-testid="protocol-description" />
              </div>
              <div>
                <label className="text-xs uppercase opacity-70">Applies to equipment types</label>
                <p className="text-[11px] opacity-60 mt-0.5 mb-1.5">Leave all unchecked to make this the general default protocol.</p>
                <div className="flex flex-wrap gap-2">
                  {EQUIPMENT_TYPE_PRESETS.map((t) => {
                    const on = form.applies_to_type_names.includes(t.name);
                    return (
                      <button type="button" key={t.name} onClick={() => toggleType(t.name)}
                        className="vb-pill text-xs" data-testid={`protocol-type-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        style={{ cursor: "pointer", background: on ? "var(--vb-accent)" : "var(--vb-bg-2)", color: on ? "#fff" : "inherit" }}>
                        {on && <CheckCircle2 size={11} className="inline mr-1" />}{t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase opacity-70">Cleaning steps *</label>
                <div className="space-y-2 mt-1.5" data-testid="protocol-steps">
                  {form.steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <GripVertical size={14} className="opacity-30 flex-shrink-0" />
                      <input className="vb-input" value={s} onChange={(e) => setStep(i, e.target.value)}
                        placeholder={`Step ${i + 1}`} data-testid={`protocol-step-${i}`} />
                      <button type="button" className="vb-btn ghost sm" onClick={() => removeStep(i)}
                        disabled={form.steps.length === 1} data-testid={`protocol-step-remove-${i}`}><X size={13} /></button>
                    </div>
                  ))}
                </div>
                <button type="button" className="vb-btn ghost sm mt-2" onClick={addStep} data-testid="protocol-add-step"><Plus size={13} /> Add step</button>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="protocol-active" />
                Active (available to operators)
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="vb-btn ghost" onClick={() => setForm(null)}>Cancel</button>
                <button type="submit" className="vb-btn" disabled={busy} data-testid="protocol-save">
                  {busy ? "Saving…" : editingId ? "Save changes" : "Create protocol"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
